/**
 * InterviewQuestionPanel
 * Question navigation UI — global timer replaces per-question countdown.
 *
 * Props:
 *   questions    : array  — { question, category?, section?, section_slug? }
 *   currentIndex : number — controlled from parent
 *   onNext       : fn()  — advance to next question (parent increments index)
 *   onPrev       : fn()  — go to previous question; disabled on first question
 *   onFinish     : fn()  — called on last question's "Finish" button
 *   isLoading    : bool
 *   isError      : bool
 *   onRetry      : fn()
 *   onBack       : fn()
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { WifiOff, FileQuestion, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { interpolate } from '../../../utils/cabinI18n';

// ─── Section Banner ───────────────────────────────────────────────────────────
function SectionBanner({ sectionLabel, remainingInSection, reduced, t }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={sectionLabel}
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
        animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
        transition={
          reduced
            ? { duration: 0.15 }
            : { type: 'spring', damping: 18, stiffness: 200 }
        }
        className="flex items-center gap-2 bg-sky-50 border-2 border-sky-200 rounded-xl px-4 py-2.5 mb-3"
        role="status"
        aria-live="polite"
      >
        <Award size={16} className="text-sky-600 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-sky-700 truncate">
            {interpolate(t('interviewSim.questionTransition.sectionTitle'), { section: sectionLabel })}
          </p>
          <p className="text-xs text-sky-600">
            {remainingInSection === 0
              ? t('interviewSim.questionTransition.lastInSection')
              : interpolate(t('interviewSim.questionTransition.remaining'), { count: remainingInSection })}
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Saved Transition Message ─────────────────────────────────────────────────
// Preserved for potential future use; currently not triggered (no per-question timer).
function SavedMessage({ show }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="saved"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center bg-emerald-50/95 rounded-2xl z-10"
          role="status"
          aria-live="polite"
        >
          <p className="text-emerald-700 font-bold text-base px-6 text-center">
            {show}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── All Questions Answered Screen ────────────────────────────────────────────
function AllAnsweredScreen({ onFinish, t }) {
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center">
        <Award size={28} className="text-emerald-500" aria-hidden="true" />
      </div>
      <div>
        <h3 className="font-display text-lg font-black text-[var(--text-main)] mb-1">
          {t('interviewSim.allQuestionsAnswered.title')}
        </h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
          {t('interviewSim.allQuestionsAnswered.sub')}
        </p>
      </div>
      <motion.button
        type="button"
        onClick={onFinish}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="px-6 py-3 rounded-2xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
      >
        {t('interviewSim.allQuestionsAnswered.cta')}
      </motion.button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function InterviewQuestionPanel({
  questions = [],
  currentIndex = 0,
  onNext,
  onPrev,
  isLoading = false,
  isError = false,
  onRetry,
  onBack,
  onFinish,
}) {
  const { t } = useLanguage();
  const reduced = useReducedMotion();

  // SavedMessage state — preserved for future use; no timer triggers it currently.
  const savedMsgRef = useRef(null);
  const [savedMsg, setSavedMsg] = React.useState(null);

  // Track which section was shown to detect section changes
  const prevSectionSlug = useRef(null);
  const [showSectionBanner, setShowSectionBanner] = React.useState(false);

  const allDone = currentIndex >= questions.length && questions.length > 0;
  const question = questions[currentIndex] || null;

  // ── Detect section change on question index shift ───────────────────────────
  useEffect(() => {
    if (!question) return;
    const slug = question.section_slug || null;
    if (slug && slug !== prevSectionSlug.current) {
      prevSectionSlug.current = slug;
      setShowSectionBanner(true);
    } else {
      setShowSectionBanner(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    // savedMsgRef holds a timeout ID (not a DOM node); capture the ref object
    // itself (not .current) so the cleanup always refers to the same ref object.
    const savedRef = savedMsgRef;
    return () => {
      clearTimeout(savedRef.current);
    };
  }, []);

  const isLastQuestion = currentIndex === questions.length - 1;

  // ── Manual advance: next question or finish ────────────────────────────────
  const handleManualAdvance = useCallback(() => {
    clearTimeout(savedMsgRef.current);
    setSavedMsg(null);
    if (isLastQuestion) {
      if (onFinish) onFinish();
    } else {
      if (onNext) onNext();
    }
  }, [isLastQuestion, onFinish, onNext]);

  // ── Previous question ──────────────────────────────────────────────────────
  // ileri-geri reset bug root cause was per-question timer reinitialising on
  // currentIndex change. With no timer here, prev/next simply move the index
  // without any timer state to corrupt.
  const handlePrev = useCallback(() => {
    if (currentIndex === 0) return;
    clearTimeout(savedMsgRef.current);
    setSavedMsg(null);
    if (onPrev) onPrev();
  }, [currentIndex, onPrev]);

  // Progress bar reflects question position, not elapsed time.
  const fillPct = questions.length > 0 ? (currentIndex / questions.length) * 100 : 0;

  // Section label helpers
  const getSectionLabel = useCallback((slug) => {
    if (!slug) return null;
    const key = `interviewSim.sections.${slug}`;
    const val = t(key);
    return val !== key ? val : slug;
  }, [t]);

  const currentSectionLabel = question?.section_slug
    ? getSectionLabel(question.section_slug)
    : null;

  const remainingInSection = question?.section_slug
    ? questions.slice(currentIndex + 1).filter(
        (q) => q.section_slug === question.section_slug
      ).length
    : 0;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3"
        role="region"
        aria-label={t('interviewSim.progress.questionLabel').replace(/\{.*?\}/g, '—')}
        aria-busy="true"
      >
        <div className="h-4 w-1/3 rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-2 w-full rounded-full bg-gray-200 animate-pulse" />
        <div className="h-16 rounded-xl bg-gray-100 animate-pulse" />
        <div className="h-3 w-2/3 rounded-lg bg-gray-200 animate-pulse" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div
        className="bg-white border-2 border-gray-200 rounded-2xl p-6 flex flex-col items-center gap-4 text-center"
        role="alert"
        aria-live="assertive"
      >
        <WifiOff size={36} className="text-rose-400" aria-hidden="true" />
        <div>
          <p className="font-bold text-[var(--text-main)] mb-1">
            {t('interviewSim.errors.questionsLoadFailed.title')}
          </p>
          <p className="text-sm text-gray-500">
            {t('interviewSim.errors.questionsLoadFailed.sub')}
          </p>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="px-5 py-2 rounded-xl bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
          >
            {t('cabin.error.cta')}
          </button>
        )}
      </div>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────────
  if (questions.length === 0) {
    return (
      <div
        className="bg-white border-2 border-gray-200 rounded-2xl p-6 flex flex-col items-center gap-4 text-center"
        role="region"
        aria-label={t('interviewSim.errors.emptyQuestions.title')}
      >
        <FileQuestion size={36} className="text-gray-400" aria-hidden="true" />
        <div>
          <p className="font-bold text-[var(--text-main)] mb-1">
            {t('interviewSim.errors.emptyQuestions.title')}
          </p>
          <p className="text-sm text-gray-500">
            {t('interviewSim.errors.emptyQuestions.sub')}
          </p>
        </div>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2 rounded-xl bg-white border-2 border-gray-300 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
          >
            {t('interviewSim.errors.emptyQuestions.cta')}
          </button>
        )}
      </div>
    );
  }

  // ── All done ───────────────────────────────────────────────────────────────
  if (allDone) {
    return (
      <div
        className="bg-white border-2 border-gray-200 rounded-2xl p-5"
        role="region"
        aria-label={t('interviewSim.allQuestionsAnswered.title')}
      >
        <AllAnsweredScreen onFinish={onFinish} t={t} />
      </div>
    );
  }

  // ── Normal render ──────────────────────────────────────────────────────────
  return (
    <div
      className="relative bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3"
      role="region"
      aria-label={interpolate(t('interviewSim.progress.questionLabel'), {
        current: currentIndex + 1,
        total: questions.length,
      })}
    >
      {/* Saved transition overlay — shown when savedMsg is set */}
      <SavedMessage show={savedMsg} />

      {/* Section banner */}
      {showSectionBanner && currentSectionLabel && (
        <SectionBanner
          sectionLabel={currentSectionLabel}
          remainingInSection={remainingInSection}
          reduced={reduced}
          t={t}
        />
      )}

      {/* Progress header */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
          {interpolate(t('interviewSim.progress.questionLabel'), {
            current: currentIndex + 1,
            total: questions.length,
          })}
        </span>
        {/* Section chip — only if section_slug present */}
        {currentSectionLabel && (
          <span className="text-xs bg-sky-100 text-sky-700 font-semibold px-2 py-0.5 rounded-full border border-sky-200">
            {currentSectionLabel}
          </span>
        )}
      </div>

      {/* Progress bar — shows question position, not elapsed timer */}
      <div
        className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(fillPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('interviewSim.progress.questionProgress')}
      >
        <div
          className="h-full rounded-full bg-primary transition-none"
          style={{ width: `${fillPct}%` }}
        />
      </div>

      {/* Question text — animated on index change */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={reduced ? { opacity: 0 } : { opacity: 0, x: -30 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, x: 30 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="flex flex-col gap-2"
        >
          <p className="text-[var(--text-main)] font-semibold text-base leading-snug">
            {question?.question}
          </p>
          {question?.category && (
            <span className="text-xs text-gray-400 bg-white border border-gray-200 rounded-full px-2 py-0.5 self-start">
              {(() => {
                const key = `interview.sim.cat.${question.category}`;
                const translated = t(key);
                if (translated && translated !== key) return translated;
                return t('interview.sim.cat.general') || 'General';
              })()}
            </span>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons: Prev (left) + Next/Finish (right) */}
      {(onFinish || onNext) && (
        <div className="mt-1 flex gap-2">
          {/* Previous Question */}
          <motion.button
            type="button"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            whileHover={currentIndex !== 0 ? { scale: 1.02 } : {}}
            whileTap={currentIndex !== 0 ? { scale: 0.98 } : {}}
            className="flex-1 py-2.5 rounded-xl border-2 border-gray-300 bg-white text-gray-700 font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.08)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={t('interviewSim.previousQuestion')}
          >
            <ChevronLeft size={18} aria-hidden="true" />
            {t('interviewSim.previousQuestion')}
          </motion.button>

          {/* Next Question / Finish Recording */}
          <motion.button
            type="button"
            onClick={handleManualAdvance}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 py-2.5 rounded-xl border-2 text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-1.5"
            style={{
              backgroundColor: 'var(--brand-primary)',
              borderColor: 'var(--brand-primary-shadow)',
            }}
            aria-label={isLastQuestion ? t('interviewSim.allQuestionsAnswered.cta') : t('interviewSim.nextQuestion')}
          >
            {isLastQuestion ? t('interviewSim.allQuestionsAnswered.cta') : t('interviewSim.nextQuestion')}
            <ChevronRight size={18} aria-hidden="true" />
          </motion.button>
        </div>
      )}
    </div>
  );
}
